import { EnhancedBaseService } from '../base/EnhancedBaseService';
import type { ServiceResponse } from '../base/ServiceTypes';

export interface ChatSession {
  id: string;
  userId: string;
  title: string | null;
  messages: unknown[];
  settings: Record<string, unknown>;
  campaignContext: Record<string, unknown> | null;
  activeRefs: Record<string, unknown> | null;
  /**
   * BV-1 W5: per-session voice pin. NULL means "fall through to project pin
   * (when planId is set) > localStorage default". Written via the picker
   * (debounced 300ms by useBrandVoiceSessionSync) and read on hydrate.
   */
  activeBrandVoiceId: string | null;
  status: 'active' | 'completed' | 'archived';
  createdAt: string;
  updatedAt: string;
}

export interface UpdateChatSessionInput {
  messages?: unknown[];
  settings?: Record<string, unknown>;
  title?: string;
  status?: 'active' | 'completed' | 'archived';
  campaignContext?: Record<string, unknown>;
  activeRefs?: Record<string, unknown>;
}

class ChatSessionServiceClass extends EnhancedBaseService {
  // Conversations are author-private again (20260710193000_conversations_author_private_revert):
  // chat_sessions RLS is `user_id = auth.uid()`. Owner-resolution here was correct only while
  // the table was owner-scoped; under author-private policies it would write a teammate's
  // session under the ACCOUNT OWNER's id and then fail to read it back. Filter and insert with
  // the raw caller id.

  async getActiveSession(
    userId: string,
    excludeWithCampaign = false,
  ): Promise<ServiceResponse<ChatSession | null>> {
    return this.execute(async () => {
      let query = this.client
        .from('chat_sessions')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'active');

      if (excludeWithCampaign) {
        query = query.is('project_id', null);
      }

      const { data, error } = await query
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw new Error(error.message);
      return data ? this.mapRow(data) : null;
    });
  }

  async getSessionByCampaignId(
    userId: string,
    campaignId: string,
  ): Promise<ServiceResponse<ChatSession | null>> {
    return this.execute(async () => {
      const { data, error } = await this.client
        .from('chat_sessions')
        .select('*')
        .eq('user_id', userId)
        .eq('project_id', campaignId)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw new Error(error.message);
      return data ? this.mapRow(data) : null;
    });
  }

  async getSession(sessionId: string): Promise<ServiceResponse<ChatSession | null>> {
    return this.execute(async () => {
      const { data, error } = await this.client
        .from('chat_sessions')
        .select('*')
        .eq('id', sessionId)
        .maybeSingle();

      if (error) throw new Error(error.message);
      return data ? this.mapRow(data) : null;
    });
  }

  async createSession(userId: string): Promise<ServiceResponse<ChatSession>> {
    return this.execute(async () => {
      const { data, error } = await this.client
        .from('chat_sessions')
        .insert({ user_id: userId })
        .select()
        .single();

      if (error) throw new Error(error.message);
      return this.mapRow(data);
    });
  }

  async updateSession(
    sessionId: string,
    input: UpdateChatSessionInput,
  ): Promise<ServiceResponse<ChatSession>> {
    return this.execute(async () => {
      const updatePayload: Record<string, unknown> = {
        updated_at: new Date().toISOString(),
      };

      if (input.messages !== undefined) updatePayload.messages = input.messages;
      if (input.settings !== undefined) updatePayload.settings = input.settings;
      if (input.title !== undefined) updatePayload.title = input.title;
      if (input.status !== undefined) updatePayload.status = input.status;
      if (input.campaignContext !== undefined)
        updatePayload.project_id =
          (input.campaignContext as { campaignId?: string } | null)?.campaignId ?? null;
      if (input.activeRefs !== undefined) updatePayload.active_refs = input.activeRefs;

      const { data, error } = await this.client
        .from('chat_sessions')
        .update(updatePayload)
        .eq('id', sessionId)
        .select()
        .single();

      if (error) throw new Error(error.message);
      return this.mapRow(data);
    });
  }

  /**
   * BV-1 W5: scoped UPDATE on `active_brand_voice_id` ONLY.
   *
   * Deliberately does NOT bump `updated_at` — every picker keypress would
   * otherwise re-order the recent-sessions list and churn cache. Defense-in-
   * depth `.eq('user_id', userId)` on top of RLS so a stolen sessionId can't
   * write across tenants if RLS is ever misconfigured.
   *
   * `voiceId === null` clears the pin (falls through to project / default
   * resolution at read time per the W4 priority chain).
   */
  async updateActiveBrandVoiceId(
    sessionId: string,
    userId: string,
    voiceId: string | null,
  ): Promise<ServiceResponse<null>> {
    return this.execute(async () => {
      const { error } = await this.client
        .from('chat_sessions')
        .update({ active_brand_voice_id: voiceId })
        .eq('id', sessionId)
        .eq('user_id', userId);

      if (error) throw new Error(error.message);
      return null;
    });
  }

  async listSessions(userId: string, limit = 20): Promise<ServiceResponse<ChatSession[]>> {
    return this.execute(async () => {
      const { data, error } = await this.client
        .from('chat_sessions')
        .select('*')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false })
        .limit(limit);

      if (error) throw new Error(error.message);
      return (data || []).map((row: Record<string, unknown>) => this.mapRow(row));
    });
  }

  private mapRow(row: Record<string, unknown>): ChatSession {
    return {
      id: row.id as string,
      userId: row.user_id as string,
      title: (row.title as string) || null,
      messages: (row.messages as unknown[]) || [],
      settings: (row.settings as Record<string, unknown>) || {},
      campaignContext: row.project_id ? { campaignId: row.project_id as string } : null,
      activeRefs: (row.active_refs as Record<string, unknown>) || null,
      activeBrandVoiceId: (row.active_brand_voice_id as string) ?? null,
      status: row.status as ChatSession['status'],
      createdAt: row.created_at as string,
      updatedAt: row.updated_at as string,
    };
  }
}

export const chatSessionService = new ChatSessionServiceClass();
